import { getGameAudioBuses } from '../../client/game-audio.js'

let globalMuted = false

function getCtx(): AudioContext | null {
  try {
    return getGameAudioBuses('bubble-pop').ctx
  } catch {
    return null
  }
}

function getSfxBusNode(): GainNode {
  return getGameAudioBuses('bubble-pop').sfx
}

function createEnvelope(
  context: AudioContext,
  startTime: number,
  duration: number,
  volume: number,
  attack: number = 0.01,
  release: number = Math.min(0.2, duration * 0.6),
): GainNode {
  const gain = context.createGain()
  const peakTime = startTime + Math.max(attack, 0.008)
  const releaseStart = Math.max(peakTime + 0.01, startTime + duration - Math.max(release, 0.04))

  gain.gain.setValueAtTime(0.0001, startTime)
  gain.gain.linearRampToValueAtTime(volume, peakTime)
  gain.gain.exponentialRampToValueAtTime(Math.max(volume * 0.82, 0.0002), releaseStart)
  gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration)

  return gain
}

function playTone(
  frequency: number,
  duration: number,
  type: OscillatorType = 'sine',
  volume: number = 0.1,
  delay: number = 0,
): void {
  if (globalMuted) return
  const context = getCtx()
  if (!context) return

  const startTime = context.currentTime + delay
  const oscillator = context.createOscillator()
  const gain = createEnvelope(context, startTime, duration, volume)

  oscillator.type = type
  oscillator.frequency.setValueAtTime(frequency, startTime)
  oscillator.connect(gain)
  gain.connect(getSfxBusNode())

  oscillator.start(startTime)
  oscillator.stop(startTime + duration + 0.05)
}

function playNoise(duration: number, volume: number = 0.1, filterFreq: number = 800): void {
  if (globalMuted) return
  const context = getCtx()
  if (!context) return

  const bufferSize = context.sampleRate * duration
  const buffer = context.createBuffer(1, bufferSize, context.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1
  }

  const noise = context.createBufferSource()
  noise.buffer = buffer

  const filter = context.createBiquadFilter()
  filter.type = 'lowpass'
  filter.frequency.value = filterFreq

  const gain = createEnvelope(context, context.currentTime, duration, volume, 0.02, 0.15)

  noise.connect(filter)
  filter.connect(gain)
  gain.connect(getSfxBusNode())

  noise.start()
  noise.stop(context.currentTime + duration + 0.05)
}

export function sfxBubblePop(): void {
  // Satisfying pop: rising tone + brief noise burst
  playTone(600, 0.08, 'sine', 0.12)
  playTone(900, 0.06, 'sine', 0.08, 0.02)
  playNoise(0.06, 0.05, 2000)
}

export function sfxBubbleSpawn(): void {
  // Gentle bubble forming sound
  playTone(300, 0.1, 'sine', 0.03)
}

let ambienceOscillators: OscillatorNode[] | null = null
let ambienceGain: GainNode | null = null

export function startAmbience(): void {
  if (globalMuted) return
  const context = getCtx()
  if (!context) return

  stopAmbience()

  ambienceGain = context.createGain()
  ambienceGain.gain.setValueAtTime(0.0001, context.currentTime)
  ambienceGain.gain.linearRampToValueAtTime(0.025, context.currentTime + 2)
  ambienceGain.connect(getSfxBusNode())

  // Gentle aquatic pad
  const freqs = [110, 165, 220]
  const types: OscillatorType[] = ['sine', 'triangle', 'sine']

  ambienceOscillators = freqs.map((f, i) => {
    const osc = context.createOscillator()
    osc.type = types[i]
    osc.frequency.setValueAtTime(f, context.currentTime)
    osc.connect(ambienceGain!)
    osc.start()
    return osc
  })
}

export function stopAmbience(): void {
  if (ambienceOscillators) {
    for (const osc of ambienceOscillators) {
      try { osc.stop() } catch { /* already stopped */ }
    }
    ambienceOscillators = null
  }
  if (ambienceGain) {
    try { ambienceGain.disconnect() } catch { /* already disconnected */ }
    ambienceGain = null
  }
}

export function setMuted(muted: boolean): void {
  globalMuted = muted
  if (muted) stopAmbience()
}